/**
 * DRI table column filter menus — thin wrappers around `UnifiedColumnSortFilter` for consistent UI.
 */
import React from 'react';
import { Box, TextField, InputAdornment } from '@mui/material';
import FilterListAltIcon from '../common/FilterListAltIcon';
import UnifiedColumnSortFilter from '../filters/UnifiedColumnSortFilter';

const MENU_ANCHOR = { vertical: 'bottom' as const, horizontal: 'left' as const };
const MENU_TRANSFORM = { vertical: 'top' as const, horizontal: 'left' as const };

// -----------------------------------------------------------------------------
// Checkbox filter
// -----------------------------------------------------------------------------

export interface CheckboxColumnFilterMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  columnLabel: string;
  options: string[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onApply: () => void;
  hasFilter: boolean;
  onClearFilter: () => void;
}

export const CheckboxColumnFilterMenu: React.FC<CheckboxColumnFilterMenuProps> = ({
  open,
  anchorEl,
  onClose,
  columnLabel,
  options,
  selectedValues,
  onToggle,
  searchText,
  onSearchTextChange,
  onSelectAll,
  onClearAll,
  onApply,
  hasFilter,
  onClearFilter,
}) => (
  <UnifiedColumnSortFilter
    anchorOrigin={MENU_ANCHOR}
    transformOrigin={MENU_TRANSFORM}
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    columnKey={columnLabel}
    title={columnLabel}
    isSortable={false}
    onSort={() => {}}
    forceShowFilterTab
    enableSearch
    filterOptions={options}
    textFilterControlled
    controlledSearchTerm={searchText}
    onControlledSearchChange={onSearchTextChange}
    controlledSelectedValues={selectedValues}
    onToggleControlledValue={onToggle}
    onControlledSelectAll={onSelectAll}
    onControlledClearSelections={onClearAll}
    showHeaderClearIcon={hasFilter}
    onHeaderClearFilter={() => {
      onClearFilter();
      onClose();
    }}
    primaryFooterLabel="Apply"
    secondaryFooterLabel="Close"
    secondaryFooterVariant="outlined"
    showSelectionCountInApply={false}
    footerPrimaryAction={onApply}
    footerSecondaryAction={onClose}
  />
);

// -----------------------------------------------------------------------------
// Numeric range filter
// -----------------------------------------------------------------------------

export interface NumericRangeColumnFilterMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  columnLabel: string;
  rangeHint?: string;
  min: number | null;
  max: number | null;
  onMinChange: (value: number | null) => void;
  onMaxChange: (value: number | null) => void;
  onApply: () => void;
  onClear: () => void;
  hasFilter: boolean;
}

export const NumericRangeColumnFilterMenu: React.FC<NumericRangeColumnFilterMenuProps> = ({
  open,
  anchorEl,
  onClose,
  columnLabel,
  rangeHint,
  min,
  max,
  onMinChange,
  onMaxChange,
  onApply,
  onClear,
  hasFilter,
}) => (
  <UnifiedColumnSortFilter
    anchorOrigin={MENU_ANCHOR}
    transformOrigin={MENU_TRANSFORM}
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    columnKey={columnLabel}
    title={columnLabel}
    isSortable={false}
    onSort={() => {}}
    isRangeFilter
    minValue={min}
    maxValue={max}
    numericRangeHint={rangeHint}
    onRangeMinInputChange={onMinChange}
    onRangeMaxInputChange={onMaxChange}
    omitAllClearHeader
    showHeaderClearIcon={hasFilter}
    onHeaderClearFilter={onClear}
    primaryFooterLabel="Apply"
    secondaryFooterLabel="Clear"
    secondaryFooterVariant="outlined"
    showSelectionCountInApply={false}
    footerPrimaryAction={onApply}
    footerSecondaryAction={onClear}
  />
);

// -----------------------------------------------------------------------------
// Date range filter
// -----------------------------------------------------------------------------

export interface DateRangeColumnFilterMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  columnLabel: string;
  startDate: Date | null;
  endDate: Date | null;
  /** Commits draft dates when user clicks Apply (menu closes via unified footer). */
  onDateFilterApply: (startDate: Date | null, endDate: Date | null) => void;
  hasFilter: boolean;
  onClearFilter: () => void;
}

export const DateRangeColumnFilterMenu: React.FC<DateRangeColumnFilterMenuProps> = ({
  open,
  anchorEl,
  onClose,
  columnLabel,
  startDate,
  endDate,
  onDateFilterApply,
  hasFilter,
  onClearFilter,
}) => (
  <UnifiedColumnSortFilter
    anchorOrigin={MENU_ANCHOR}
    transformOrigin={MENU_TRANSFORM}
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    columnKey={columnLabel}
    title={columnLabel}
    isSortable={false}
    onSort={() => {}}
    forceShowFilterTab
    isDateFilter
    dateFilter={{ startDate, endDate }}
    onDateFilterApply={onDateFilterApply}
    showHeaderClearIcon={hasFilter}
    onHeaderClearFilter={onClearFilter}
    primaryFooterLabel="Apply"
    secondaryFooterLabel="Cancel"
    secondaryFooterVariant="outlined"
    showSelectionCountInApply={false}
  />
);

// -----------------------------------------------------------------------------
// Text search filter
// -----------------------------------------------------------------------------

export interface TextSearchColumnFilterMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  columnLabel: string;
  value: string;
  onChange: (value: string) => void;
  hasFilter: boolean;
  onClearFilter: () => void;
  onApply?: () => void;
}

export const TextSearchColumnFilterMenu: React.FC<TextSearchColumnFilterMenuProps> = ({
  open,
  anchorEl,
  onClose,
  columnLabel,
  value,
  onChange,
  hasFilter,
  onClearFilter,
  onApply,
}) => (
  <UnifiedColumnSortFilter
    anchorOrigin={MENU_ANCHOR}
    transformOrigin={MENU_TRANSFORM}
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    columnKey={columnLabel}
    title={columnLabel}
    isSortable={false}
    onSort={() => {}}
    forceShowFilterTab
    omitAllClearHeader
    showHeaderClearIcon={hasFilter}
    onHeaderClearFilter={onClearFilter}
    customFilterContent={
      <TextField
        size="small"
        fullWidth
        placeholder={`Search ${columnLabel.toLowerCase()}...`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FilterListAltIcon sx={{ fontSize: '1rem' }} />
            </InputAdornment>
          ),
        }}
        sx={{ '& .MuiInputBase-input': { py: 0.65, fontSize: '0.75rem' } }}
      />
    }
    primaryFooterLabel={onApply ? 'Apply' : 'Close'}
    secondaryFooterLabel="Clear"
    secondaryFooterVariant="text"
    showSelectionCountInApply={false}
    footerPrimaryAction={onApply ?? onClose}
    footerSecondaryAction={onClearFilter}
  />
);
